/* eslint-disable camelcase */
/**
 * <free-gift-chooser> — the "Choose your FREE gift" card in the cart
 * drawer for `choose`-mode promos (see snippets/cart-free-gift-chooser.liquid).
 *
 * Selection state is derived from the cart and rendered server-side, so
 * this element is thin: it wires the add / swap buttons to
 * `window.freeGiftHandler.selectGift` / `swapGift`. The element is
 * recreated on every cart re-render, so all binding happens in
 * connectedCallback.
 *
 * The "Swap" link lives on the nested gift line (outside this element),
 * so a single delegated document listener reveals the matching hidden
 * chooser when it's clicked.
 */
if (!customElements.get('free-gift-chooser')) {
  class FreeGiftChooser extends HTMLElement {
    connectedCallback () {
      this.slug = this.dataset.slug || ''
      this.addEventListener('click', this.handleClick.bind(this))
    }

    get selectedVariant () {
      return this.dataset.selectedVariant || ''
    }

    async handleClick (event) {
      const button = event.target.closest('[data-fg-select]')
      if (!button || !this.contains(button)) return
      event.preventDefault()

      const handler = window.freeGiftHandler
      if (!handler || typeof handler.selectGift !== 'function') return

      const slug = button.dataset.slug || this.slug
      const variantId = button.dataset.variantId
      if (!slug || !variantId) return

      const current = this.selectedVariant
      if (current && current === String(variantId)) {
        this.close()
        return
      }

      this.setLoading(true)
      try {
        if (current) {
          await handler.swapGift(slug, current, variantId)
        } else {
          await handler.selectGift(slug, variantId)
        }
      } finally {
        this.setLoading(false)
      }
    }

    setLoading (isLoading) {
      if (isLoading) {
        this.setAttribute('loading', '')
      } else {
        this.removeAttribute('loading')
      }
      this.querySelectorAll('[data-fg-select]').forEach((btn) => {
        btn.disabled = isLoading
      })
    }

    reveal () {
      this.classList.remove('hidden')
      // Cross-sells are only meant to show once a gift choice is settled
      // (see cart-cross-sells.liquid / cart-drawer.liquid gating on
      // `cart_has_unselected_choose`). That gate is server-rendered and
      // only re-evaluated on a full cart re-render, so a bare reveal via
      // the "Swap" link (no cart change yet) has to hide it client-side
      // too — otherwise cross-sells stays visible from before the swap
      // started until the customer finishes picking a new gift.
      document.querySelectorAll('[data-cross-sells-root]').forEach((el) => {
        el.classList.add('hidden')
      })
    }

    close () {
      if (this.selectedVariant) {
        this.classList.add('hidden')
        document.querySelectorAll('[data-cross-sells-root]').forEach((el) => {
          el.classList.remove('hidden')
        })
      }
    }
  }

  customElements.define('free-gift-chooser', FreeGiftChooser)

  // Reveal the matching (hidden) chooser when a "Swap" link on a nested
  // gift line is clicked. Attached once at module load; the gift lines
  // are re-rendered on every cart update but the document persists.
  document.addEventListener('click', (event) => {
    const swap = event.target.closest('[data-fg-swap-reveal]')
    if (!swap) return
    event.preventDefault()
    const slug = swap.dataset.slug
    if (!slug) return
    const chooser = document.querySelector(
      `free-gift-chooser[data-slug="${CSS.escape(slug)}"]`,
    )
    if (chooser && typeof chooser.reveal === 'function') {
      chooser.reveal()
      chooser.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  })
}
